import { useContext } from "react";
import { usePTS } from "../../context/PTSContext";
import { useRakennusData } from "../../context/RakennusDataContext";
import { ReportTemplateContext } from "../report/ReportTemplate/ReportTemplateContext";
import { buildBucketedRows, buildImmediateRows, getArea } from "./toimenpiteetTekstimuodossaUtils";

// Live-reading component, same family as CapexYhteenveto.jsx — reads
// usePTS()/useRakennusData()/ReportTemplateContext directly rather than
// taking { data, onChange }, since the numbers already exist in the PTS
// system. A plain, readable itemized list derived from the same data
// Tekniikkataulut/Capex-yhteenveto already show as grids/charts — one row
// per (year, category, kohde, cost), bucketed into 0–1/2–5/6–10 vuotta,
// led by a "Välittömästi / ennen kauppaa" list (category + kohde only, no
// year/cost) built from every "Välitön"-flagged toimenpide in the whole
// report (only shown when at least one exists).

const fmtEur = (v) => `${Math.round(v).toLocaleString("fi-FI")} €`;

// Dedicated 2-column table for the "Välittömästi / ennen kauppaa" list —
// no Vuosi/Kustannus columns and no subtotal bar, since buildImmediateRows
// carries neither a year nor a cost for these items.
function ImmediateTable({ bucket }) {
  return (
    <div className="mb-4">
      <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#1c1c1e", marginBottom: "0.6rem" }}>
        {bucket.label}
      </h3>
      <div className="table-responsive">
        <table className="table table-sm mb-0" style={{ fontSize: "0.82rem" }}>
          <thead>
            <tr>
              <th style={{ background: "#eafbf1", color: "#198754" }}>Kategoria</th>
              <th style={{ background: "#eafbf1", color: "#198754" }}>Kohde</th>
            </tr>
          </thead>
          <tbody>
            {bucket.rows.map((r) => (
              <tr key={r.id}>
                <td>{r.category}</td>
                <td>{r.kohde}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BucketTable({ bucket }) {
  return (
    <div className="mb-4">
      <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#1c1c1e", marginBottom: "0.6rem" }}>
        {bucket.label}
      </h3>
      <div className="table-responsive">
      <table className="table table-sm mb-0" style={{ fontSize: "0.82rem" }}>
        <thead>
          <tr>
            <th style={{ background: "#eafbf1", color: "#198754" }}>Vuosi</th>
            <th style={{ background: "#eafbf1", color: "#198754" }}>Kategoria</th>
            <th style={{ background: "#eafbf1", color: "#198754" }}>Kohde</th>
            <th style={{ background: "#eafbf1", color: "#198754" }} className="text-end">Kustannus (€)</th>
          </tr>
        </thead>
        <tbody>
          {bucket.rows.map((r) => (
            <tr key={r.id}>
              <td>{r.year}</td>
              <td>{r.category}</td>
              <td>{r.kohde}</td>
              <td className="text-end">{fmtEur(r.costEur)}</td>
            </tr>
          ))}
          {bucket.rows.length === 0 && (
            <tr><td colSpan={4} className="text-center text-muted">Ei toimenpiteitä tällä aikavälillä</td></tr>
          )}
          <tr className="fw-bold" style={{ background: "#198754", color: "#fff" }}>
            <td colSpan={3}>Osasumma — {bucket.label}</td>
            <td className="text-end">{fmtEur(bucket.subtotalEur)}</td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>
  );
}

export default function ToimenpiteetTekstimuodossa({ data } = {}) {
  const ptsContext = usePTS() || {};
  const { rakennusData } = useRakennusData();
  const reportContext = useContext(ReportTemplateContext);
  const mergedRakennusData = reportContext?.mergedRakennusData;
  const groupedToimenpiteet = reportContext?.groupedToimenpiteet;

  if (!ptsContext.years?.length) return null; // PTS data not loaded yet

  // Label-only — every figure below is always computed VAT 0% regardless of
  // this toggle; it only picks which phrasing is printed in the grand-total
  // caption. Same pattern as CapexYhteenveto.jsx's own ALVITON setting.
  const alviton = !!data?.alviton;
  const alvLabel = alviton ? "ALV 0 %" : "sis. ALV 25,5%";

  const { buckets, grandTotalEur } = buildBucketedRows(ptsContext);
  const immediateBucket = buildImmediateRows(groupedToimenpiteet);
  const grossArea = getArea(rakennusData, mergedRakennusData, "kokonaisala");
  const perM2Over10y = grandTotalEur / grossArea;

  return (
    <div className="mb-4 mx-auto w-100" style={{ maxWidth: "960px" }}>
      <div className="border rounded p-3" style={{ background: "#fff" }}>
        <div
          className="mb-3"
          style={{ fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#198754" }}
        >
          Toimenpiteet tekstimuodossa
        </div>

        {immediateBucket.rows.length > 0 && (
          <ImmediateTable bucket={immediateBucket} />
        )}

        {buckets.map((bucket) => (
          <BucketTable key={bucket.label} bucket={bucket} />
        ))}

        <div className="border rounded p-3" style={{ background: "#eafbf1" }}>
          <div className="fw-bold" style={{ color: "#198754", fontSize: "1.05rem" }}>
            Toimenpiteiden kustannusarvio yhteensä (10 vuotta): {fmtEur(grandTotalEur)}
          </div>
          <div className="text-muted" style={{ fontSize: "0.8rem" }}>
            {fmtEur(perM2Over10y)}/m² (10 vuotta), {grossArea.toLocaleString("fi-FI")} m² kokonaisala, {alvLabel}
          </div>
        </div>
      </div>
    </div>
  );
}
